/**
 * Unified API Client for AIghtBet
 * ==============================
 * Single source of truth for all API interactions
 * Consolidates api.ts and apiClient.ts functionality
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { getApiUrl, CONFIG } from '../config/api';

// API URL configuration is now centralized in config/api.ts

interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableStatuses: number[];
}

interface RequestMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
  retryCount: number;
  url: string;
  method: string;
}

interface TokenProvider {
  getToken: () => string | null;
  clearToken: () => void;
  isTokenExpired: () => boolean;
}

interface GameParams {
  week?: number;
  team?: string;
  conference?: string;
  limit?: number;
  season?: number;
  status?: string;
  sport?: string;  // Added sport parameter
}

interface PredictionParams {
  game_id?: string;
  bet_type?: string;
  confidence_threshold?: number;
  sport?: string;  // Added sport parameter
}


class UnifiedAPIClient {
  private client: AxiosInstance;
  private retryConfig: RetryConfig;
  private requestMetrics: Map<string, RequestMetrics>;
  private performanceThreshold: number = 1000; // 1 second
  private baseURL: string;
  private tokenProvider: TokenProvider | null = null;
  private authFailureCount: number = 0;
  private authCircuitOpen: boolean = false;
  private lastAuthFailure: number = 0;
  private requestQueue: Array<{ execute: () => Promise<any>; resolve: (value: any) => void; reject: (error: any) => void; key?: string }> = [];
  private activeRequests: number = 0;
  private maxConcurrentRequests: number = 5; // Reduced for better stability
  private requestRateLimit: number = 10; // requests per second
  private requestTimestamps: number[] = [];
  private processingQueue: boolean = false;
  private pendingRequests: Map<string, Promise<any>> = new Map(); // Request deduplication
  private currentSport: string = 'college_football'; // Default sport context

  constructor() {
    this.baseURL = getApiUrl();

    this.retryConfig = {
      maxRetries: 2, // Reduced from 3 to prevent excessive retries
      initialDelay: 500, // Reduced from 1000ms to 500ms for faster retries
      maxDelay: 5000, // Reduced from 30000ms to 5000ms max wait
      backoffMultiplier: 2,
      retryableStatuses: [408, 429, 502, 503, 504], // Removed 500 to prevent retrying server errors
    };

    this.requestMetrics = new Map();

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: CONFIG.API_TIMEOUT, // Use centralized timeout configuration
      headers: CONFIG.DEFAULT_HEADERS, // Use centralized headers
    });

    this.setupInterceptors();
  }

  // Method to set token provider (called by AuthContext)
  setTokenProvider(tokenProvider: TokenProvider): void {
    this.tokenProvider = tokenProvider;
  }

  // Method to set current sport context
  setSport(sport: string): void {
    this.currentSport = sport;
    console.log(`🏆 Sport context changed to: ${sport}`);
  }

  // Method to get current sport context
  getSport(): string {
    return this.currentSport;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Check auth circuit breaker
        if (this.authCircuitOpen) {
          const timeSinceFailure = Date.now() - this.lastAuthFailure;
          if (timeSinceFailure < 30000) { // 30 second cooldown
            console.warn('🔒 Auth circuit breaker OPEN - blocking request');
            return Promise.reject(new Error('Authentication temporarily disabled due to failures'));
          } else {
            // Try to reset circuit breaker
            this.authCircuitOpen = false;
            this.authFailureCount = 0;
          }
        }

        // Rate limiting
        await this.enforceRateLimit();
        // Get auth token from token provider (preferred) or fallback to localStorage
        let token: string | null = null;
        
        if (this.tokenProvider) {
          // Use token provider if available (from AuthContext)
          if (!this.tokenProvider.isTokenExpired()) {
            token = this.tokenProvider.getToken();
          }
        } else {
          // Fallback to direct localStorage access for backwards compatibility
          token = localStorage.getItem('auth_token') || 
                 sessionStorage.getItem('auth_token');
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Track request metrics
        const requestId = this.generateRequestId(config);
        this.requestMetrics.set(requestId, {
          startTime: Date.now(),
          retryCount: 0,
          url: config.url || '',
          method: (config.method || 'GET').toUpperCase(),
        });

        (config as any).requestId = requestId;

        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Track response metrics
        const requestId = (response.config as any).requestId;
        if (requestId && this.requestMetrics.has(requestId)) {
          const metrics = this.requestMetrics.get(requestId)!;
          metrics.endTime = Date.now();
          metrics.duration = metrics.endTime - metrics.startTime;

          const method = response.config.method?.toUpperCase() || 'GET';
          const url = response.config.url || '';
          
          console.log(`✅ API Response: ${method} ${url} (${metrics.duration}ms)`);

          // Log slow requests
          if (metrics.duration > this.performanceThreshold) {
            console.warn(`🐌 Slow API request: ${url} took ${metrics.duration}ms`);
          }

          // Clean up metrics
          setTimeout(() => this.requestMetrics.delete(requestId), 60000);
        }

        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as AxiosRequestConfig & { retryCount?: number };
        const requestId = (config as any)?.requestId;

        // Log error details
        const method = config.method?.toUpperCase() || 'GET';
        const url = config.url || '';
        const status = error.response?.status;
        
        console.error(`❌ API Error: ${method} ${url} - Status: ${status}`, error);

        // Handle different error types
        if (error.response) {
          const status = error.response.status;

          // Authentication errors
          if (status === 401) {
            // Increment auth failure count
            this.authFailureCount++;
            this.lastAuthFailure = Date.now();
            
            // Open circuit breaker after 3 auth failures
            if (this.authFailureCount >= 3) {
              this.authCircuitOpen = true;
              console.error('🔒 Auth circuit breaker OPENED after 3 failures');
            }
            
            // Use token provider if available, otherwise fallback to direct clearing
            if (this.tokenProvider) {
              this.tokenProvider.clearToken();
            } else {
              this.clearAuthTokens();
            }
            
            // Only show toast once per circuit breaker cycle
            if (this.authFailureCount === 1) {
              toast.error('Session expired. Please log in again.');
            }
            
            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
              setTimeout(() => {
                window.location.href = '/login';
              }, 1000);
            }
            return Promise.reject(error);
          }

          // KAN-23634: 403s from backend routes are background-fetch failures
          // (tier-gated route, inactive account edge cases) — surfacing the raw
          // "You do not have permission" toast reads as a Postgres/RLS leak to
          // end users (Apple reviewer P0). Page-level <TierGate> handles the
          // paywall UX; calling hooks already fall through to graceful empty
          // state on rejection. Capture to Sentry so the signal survives.
          if (status === 403) {
            console.warn(`🔒 403 from ${method} ${url} — suppressed user-facing toast (logged to Sentry)`);
            Sentry.captureException(error, {
              level: 'warning',
              tags: { kind: 'suppressed_403_toast', endpoint: url },
              extra: { method, status, response: error.response?.data },
            });
            return Promise.reject(error);
          }

          // Rate limiting
          if (status === 429) {
            toast.error('Too many requests. Please wait a moment before trying again.');
          }

          // Check if we should retry
          if (this.shouldRetry(error, config)) {
            return this.retryRequest(error, config, requestId);
          }

          // Skip toast for 404s on prediction cache endpoints — missing predictions are
          // expected (no cache yet) and must not spam the user with error notifications.
          const isPredictionCacheEndpoint = url.includes('/api/predictions/');
          if (status === 404 && isPredictionCacheEndpoint) {
            return Promise.reject(error);
          }

          // Handle user-friendly error messages
          this.handleUserError(error);
        } else if (error.request) {
          // Network error
          if (this.shouldRetry(error, config)) {
            return this.retryRequest(error, config, requestId);
          }

          toast.error('Network error. Please check your connection and try again.');
        } else {
          // Unknown error
          toast.error('An unexpected error occurred');
        }

        return Promise.reject(error);
      }
    );
  }

  private shouldRetry(error: AxiosError, config: AxiosRequestConfig & { retryCount?: number }): boolean {
    // Don't retry if we've exceeded max retries
    if ((config.retryCount || 0) >= this.retryConfig.maxRetries) {
      return false;
    }

    // Don't retry authentication errors or when circuit breaker is open
    if (error.response?.status === 401 || error.response?.status === 403 || this.authCircuitOpen) {
      return false;
    }
    
    // Don't retry 500 errors (server errors should be fixed, not retried)
    if (error.response?.status === 500) {
      return false;
    }

    // Don't retry client errors (4xx except specific ones)
    if (error.response?.status && 
        error.response.status >= 400 && 
        error.response.status < 500 &&
        !this.retryConfig.retryableStatuses.includes(error.response.status)) {
      return false;
    }

    // Check if status is retryable
    if (error.response && this.retryConfig.retryableStatuses.includes(error.response.status)) {
      return true;
    }

    // Retry on network errors (no response)
    return !error.response && error.code !== 'ECONNABORTED';
  }

  private async retryRequest(
    _error: AxiosError,
    config: AxiosRequestConfig & { retryCount?: number },
    requestId: string
  ): Promise<any> {
    const retryCount = (config.retryCount || 0) + 1;
    config.retryCount = retryCount;

    // Update metrics
    if (this.requestMetrics.has(requestId)) {
      this.requestMetrics.get(requestId)!.retryCount = retryCount;
    }

    // Calculate delay with exponential backoff + jitter
    const baseDelay = this.retryConfig.initialDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount - 1);
    const jitter = Math.random() * 1000; // Add up to 1 second of jitter
    const delay = Math.min(baseDelay + jitter, this.retryConfig.maxDelay);

    console.log(`🔄 Retrying request (attempt ${retryCount}/${this.retryConfig.maxRetries}) after ${Math.round(delay)}ms`);

    // Show retry notification for longer delays
    if (delay > 3000) {
      toast.loading(`Retrying... (attempt ${retryCount}/${this.retryConfig.maxRetries})`, {
        id: `retry-${requestId}`,
        duration: delay
      });
    }

    await new Promise(resolve => setTimeout(resolve, delay));

    return this.client.request(config);
  }

  private handleUserError(error: AxiosError): void {
    const status = error.response?.status;
    const data = error.response?.data as any;

    let message = 'An error occurred';

    if (data?.detail) {
      // Handle different detail formats
      if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (typeof data.detail === 'object' && data.detail.error) {
        message = data.detail.error;
      } else if (Array.isArray(data.detail)) {
        message = data.detail[0];
      } else {
        message = JSON.stringify(data.detail);
      }
    } else if (data?.message) {
      message = data.message;
    } else if (data?.error) {
      message = data.error;
    } else {
      switch (status) {
        case 400:
          message = 'Invalid request. Please check your input.';
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 422:
          message = 'Invalid data provided. Please check your input.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 502:
          message = 'Server is temporarily unavailable.';
          break;
        case 503:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
        case 504:
          message = 'Request timeout. Please try again.';
          break;
        default:
          message = `Server error (${status}). Please try again later.`;
      }
    }

    toast.error(message);
  }

  private generateRequestId(config: AxiosRequestConfig): string {
    return `${config.method}-${config.url}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private clearAuthTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('token');
  }

  // =======================
  // CORE HTTP METHODS
  // =======================

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    // Queue prediction requests to prevent overwhelming the server
    if (url.includes('/predictions/') || url.includes('/ai/')) {
      const key = this.generateRequestKey('GET', url, config?.params);
      return this.queueRequest(async () => {
        const response = await this.client.get<T>(url, config);
        return response.data;
      }, key);
    }
    
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    // Queue prediction and AI requests to prevent overwhelming the server
    if (url.includes('/predictions/') || url.includes('/ai/')) {
      const key = this.generateRequestKey('POST', url, data);
      return this.queueRequest(async () => {
        const response = await this.client.post<T>(url, data, config);
        return response.data;
      }, key);
    }
    
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // =======================
  // UNIFIED API ENDPOINTS
  // =======================

  // Games API
  async getGames(params?: GameParams) {
    // Automatically include current sport context
    const enhancedParams = { ...params, sport: params?.sport || this.currentSport };
    return this.get('/api/games', { params: enhancedParams });
  }

  async getOddsGames(params?: GameParams) {
    // Automatically include current sport context
    const enhancedParams = { ...params, sport: params?.sport || this.currentSport };
    return this.get('/api/odds-games', { params: enhancedParams });
  }

  async getLiveGames(sport?: string) {
    // Include sport in query params
    const params = { sport: sport || this.currentSport };
    return this.get('/api/games/live', { params });
  }

  async getGame(gameId: string) {
    return this.get(`/api/games/${gameId}`);
  }

  async updateGameOdds(gameId: string, odds: any) {
    return this.post(`/api/games/${gameId}/odds`, odds);
  }

  // Predictions API
  async getPredictions(params?: PredictionParams) {
    // Automatically include current sport context
    const enhancedParams = { ...params, sport: params?.sport || this.currentSport };
    return this.get('/api/predictions', { params: enhancedParams });
  }

  async createPrediction(prediction: any) {
    // Add sport context to prediction data
    const enhancedPrediction = { ...prediction, sport: prediction.sport || this.currentSport };
    return this.post('/api/predictions', enhancedPrediction);
  }

  async getPrediction(predictionId: string) {
    return this.get(`/api/predictions/${predictionId}`);
  }

  async updatePredictionResult(predictionId: string, result: any) {
    return this.patch(`/api/predictions/${predictionId}/result`, result);
  }

  // AI Analysis API
  async getAIAnalysis(gameId: string) {
    return this.get(`/api/ai/analysis/${gameId}`);
  }

  async getEnsemblePrediction(gameId: string, betType: string) {
    return this.get(`/api/ai/ensemble/prediction/${gameId}/${betType}`);
  }

  async getAIHealth() {
    return this.get('/api/ai/health');
  }

  async getEnsembleStatus() {
    return this.get('/api/ai/ensemble/status');
  }

  async getRealtimeStatus() {
    return this.get('/api/ai/realtime/status');
  }

  // System Performance API (replaces individual performance tracking)
  async getSystemPerformance() {
    return this.get('/api/system/performance');
  }

  // Data Sources API
  async getDataSourcesHealth() {
    return this.get('/api/data/sources/health');
  }

  async refreshDataSources() {
    return this.post('/api/data/sources/refresh');
  }

  // KAN-86 / KAN-88 / KAN-250: legacy FastAPI auth (login/logout/refresh)
  // retired. Auth flows live in SupabaseAuthContext.tsx; getCurrentUser is
  // kept for the /api/user/me read which remains a backend endpoint.
  async getCurrentUser() {
    return this.get('/api/user/me');
  }

  // =======================
  // UTILITY METHODS
  // =======================

  setAuthToken(token: string): void {
    if (this.tokenProvider) {
      // This method is now handled by the AuthContext
      console.warn('setAuthToken called on API client, but token provider is available. Use AuthContext instead.');
    } else {
      localStorage.setItem('auth_token', token);
      sessionStorage.setItem('auth_token', token);
    }
  }

  getAuthToken(): string | null {
    if (this.tokenProvider) {
      return this.tokenProvider.getToken();
    } else {
      return localStorage.getItem('auth_token') || 
             sessionStorage.getItem('auth_token');
    }
  }

  getRequestMetrics(): RequestMetrics[] {
    return Array.from(this.requestMetrics.values());
  }

  getSlowRequests(threshold: number = this.performanceThreshold): RequestMetrics[] {
    return this.getRequestMetrics().filter(m => m.duration && m.duration > threshold);
  }

  setPerformanceThreshold(threshold: number): void {
    this.performanceThreshold = threshold;
  }

  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  private async enforceRateLimit(): Promise<void> {
    const now = Date.now();
    // Remove timestamps older than 1 second
    this.requestTimestamps = this.requestTimestamps.filter(ts => now - ts < 1000);
    
    // Check if we're at the rate limit
    if (this.requestTimestamps.length >= this.requestRateLimit) {
      const oldestTimestamp = this.requestTimestamps[0];
      const waitTime = 1000 - (now - oldestTimestamp);
      if (waitTime > 0) {
        console.log(`⏱️ Rate limit reached, waiting ${waitTime}ms`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
    
    // Add current timestamp
    this.requestTimestamps.push(now);
  }

  private async queueRequest<T>(execute: () => Promise<T>, key?: string): Promise<T> {
    // Check for duplicate requests (deduplication)
    if (key && this.pendingRequests.has(key)) {
      console.log(`♻️ Reusing pending request for: ${key}`);
      return this.pendingRequests.get(key) as Promise<T>;
    }

    // Create promise for this request
    const promise = new Promise<T>((resolve, reject) => {
      this.requestQueue.push({ execute, resolve, reject, key });
    });

    // Store in pending requests map if it has a key
    if (key) {
      this.pendingRequests.set(key, promise);
      
      // Clean up after completion
      promise.finally(() => {
        this.pendingRequests.delete(key);
      });
    }

    // Start processing queue if not already processing
    if (!this.processingQueue) {
      this.processQueue();
    }

    return promise;
  }

  private async processQueue(): Promise<void> {
    if (this.processingQueue) {
      return;
    }

    this.processingQueue = true;

    while (this.requestQueue.length > 0) {
      // Wait if we're at max concurrent requests
      while (this.activeRequests >= this.maxConcurrentRequests) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const request = this.requestQueue.shift();
      if (!request) continue;

      this.activeRequests++;

      // Execute request
      request.execute()
        .then(result => {
          request.resolve(result);
        })
        .catch(error => {
          request.reject(error);
        })
        .finally(() => {
          this.activeRequests--;
        });

      // Small delay between starting requests to prevent thundering herd
      if (this.requestQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    this.processingQueue = false;
  }

  private generateRequestKey(method: string, url: string, data?: any): string {
    // Generate a unique key for request deduplication
    const dataKey = data ? JSON.stringify(data) : '';
    return `${method.toUpperCase()}-${url}-${dataKey}`;
  }

  resetAuthCircuit(): void {
    this.authCircuitOpen = false;
    this.authFailureCount = 0;
    this.lastAuthFailure = 0;
    console.log('🔓 Auth circuit breaker RESET');
  }

  getConnectionInfo(): { baseURL: string; hasAuth: boolean; metricsCount: number; circuitOpen: boolean; queueLength: number; activeRequests: number } {
    return {
      baseURL: this.baseURL,
      hasAuth: !!this.getAuthToken(),
      metricsCount: this.requestMetrics.size,
      circuitOpen: this.authCircuitOpen,
      queueLength: this.requestQueue.length,
      activeRequests: this.activeRequests
    };
  }

  getQueueStatus(): { queueLength: number; activeRequests: number; maxConcurrent: number; pendingDeduped: number } {
    return {
      queueLength: this.requestQueue.length,
      activeRequests: this.activeRequests,
      maxConcurrent: this.maxConcurrentRequests,
      pendingDeduped: this.pendingRequests.size
    };
  }

  clearRequestQueue(): void {
    // Clear the queue and reject all pending requests
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      if (request) {
        request.reject(new Error('Request queue cleared'));
      }
    }
    this.pendingRequests.clear();
    console.log('🧹 Request queue cleared');
  }
}

// Create and export singleton instance
const unifiedApi = new UnifiedAPIClient();

// Export both the instance and class for different use cases
export { UnifiedAPIClient };
export default unifiedApi;

// For backward compatibility, also export as 'api'
export const api = unifiedApi;